import React, { FC, SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Paper, Stack, Typography } from '@mui/material';
import _pick from 'lodash/pick';
import * as yup from 'yup';

import {
  getGetProcessTalentRegistrationFormQueryKey,
  getGetProcessTalentsQueryKey,
  ProcessResponseDTOStatus,
  TalentQuestionsResponseDTO,
  useAddTalentsToProcess,
  useGetProcessById,
  useGetProcessTalentRegistrationForm,
  useGetProcessTalents,
  useUpdateProcess,
  useUpdateProcessTalentRegistrationQuestionsForm
} from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import { CommonFormFooter } from '@/components/Form/Form';
import { TabPanel, VerticalTab, VerticalTabs } from '@/components/Tabs';
import { useProcessPermissions } from '@/hooks/permissions/useProcessPermissions';
import { FieldPrefixProvider } from '@/hooks/useFieldPrefix';
import { OrganizationStructureProvider } from '@/hooks/useOrganizationStructure';
import { useProcessStatusActions } from '@/hooks/useProcessStatusActions';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import { useSnackbar } from '@/hooks/useSnackbar';
import { asPath, EDIT_MODAL_PARAM, PROCESS_SETTINGS_STEP_KEYS } from '@/root/linker';
import { borders } from '@/theme/constants';
import SettingsModalForm, { SettingsModalFormRef } from '@/views/SettingsModalForm/SettingsModalForm';

import SettingsTab from './SettingsTab/SettingsTab';
import BasicInfoTab from './BasicInfoTab';
import {
  AccessInvitationFormValues,
  accessInvitationMappingPresave,
  BasicInfoFormValues,
  createAccessInvitationDefaultValues,
  createBasicInfoDefaultValues,
  createTalentRegistrationDefaultValues,
  createTestPropertiesDefaultValues,
  DEFAULT_TALENTS,
  TalentFormValues,
  TalentRegistrationFormValues,
  TestPropertiesFormValues,
  testSettingsMappingPresave
} from './defaults';
import {
  getProcessSettingsAccessInvitationValidation,
  getProcessSettingsBasicInfoValidation,
  getProcessSettingsTalentRegistrationValidation,
  getProcessSettingsTalentsValidation,
  getProcessSettingsTestPropertiesValidation
} from './schema';
import TalentsTab from './TalentsTab';
import TestCatalogTab from './TestCatalogTab';

const SIDE_NAVIGATION_WIDTH_PX = 176;

const PARENT_TABS_MAPPING: { [key: string]: string } = {
  [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]: PROCESS_SETTINGS_STEP_KEYS.SETTINGS,
  [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION]: PROCESS_SETTINGS_STEP_KEYS.SETTINGS,
  [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]: PROCESS_SETTINGS_STEP_KEYS.SETTINGS,
  [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_MESSAGES]: PROCESS_SETTINGS_STEP_KEYS.SETTINGS
};

const DEFAULT_TABS_MAPPING: { [key: string]: string } = {
  [PROCESS_SETTINGS_STEP_KEYS.SETTINGS]: PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION
};

interface ProcessSettingsFormProps {
  parentRoutePattern: string;
}

const ProcessSettingsForm: FC<ProcessSettingsFormProps> = ({ parentRoutePattern }) => {
  const { t } = useTranslation(['settings', 'validation']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams() as { processId: string };
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);
  const [searchParams] = useSearchParams();

  const onClose = useCallback(() => {
    navigate(asPath(parentRoutePattern, params));
  }, [navigate, params, parentRoutePattern]);

  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get(EDIT_MODAL_PARAM) || PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO
  );

  const normalizeActiveTab = useCallback((tab: string) => {
    const parentTab = PARENT_TABS_MAPPING[tab as string];

    return parentTab || tab;
  }, []);

  const normalizedActiveTab = useMemo(() => normalizeActiveTab(activeTab), [activeTab, normalizeActiveTab]);

  const handleTabChange = useCallback((_event: SyntheticEvent, newValue: string) => {
    const tabToOpen = DEFAULT_TABS_MAPPING[newValue] || newValue;
    setActiveTab(tabToOpen);
  }, []);

  const { showMessage } = useSnackbar();
  const { mutateAsync: updateProcess, isLoading: isSaving } = useUpdateProcess();
  const {
    data: processData,
    isLoading: isProcessLoading,
    error: processError,
    refetch: refetchProcess
  } = useGetProcessById(params.processId, {
    query: { enabled: !!params?.processId }
  });
  const { data: talentRegistrationFormData } = useGetProcessTalentRegistrationForm(params?.processId, {
    query: {
      enabled: !!params?.processId
    }
  });

  const talentRegistrationForm = useMemo(
    () =>
      talentRegistrationFormData &&
      ({
        external: talentRegistrationFormData?.external,
        internal: talentRegistrationFormData?.internal,
        translations: talentRegistrationFormData?.translations
      } as TalentQuestionsResponseDTO),
    [talentRegistrationFormData]
  );
  // FIXME: Invent a better solution, refactor
  const { data: processTalentsList } = useGetProcessTalents(
    params.processId,
    { size: 100 },
    { query: { enabled: !!params.processId, suspense: false } }
  );

  const { mutateAsync: updateTalentRegistrationForm } = useUpdateProcessTalentRegistrationQuestionsForm();
  const { mutateAsync: addTalentsToProcess } = useAddTalentsToProcess();

  const defaultValues = useMemo(
    () =>
      processData &&
      processTalentsList &&
      talentRegistrationForm && {
        [PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO]: createBasicInfoDefaultValues(processData),
        [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION]:
          createTalentRegistrationDefaultValues(talentRegistrationForm),
        [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]: createAccessInvitationDefaultValues(processData),
        [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]: createTestPropertiesDefaultValues(processData),
        [PROCESS_SETTINGS_STEP_KEYS.TALENTS]: { ...DEFAULT_TALENTS }
      },
    [processData, processTalentsList, talentRegistrationForm]
  );

  const validations = useMemo(
    () =>
      yup
        .object()
        .shape(
          _pick(
            {
              [PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO]: getProcessSettingsBasicInfoValidation(t),
              [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION]:
                getProcessSettingsTalentRegistrationValidation(t),
              [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]: getProcessSettingsAccessInvitationValidation(t),
              [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]: getProcessSettingsTestPropertiesValidation(t),
              [PROCESS_SETTINGS_STEP_KEYS.TALENTS]: getProcessSettingsTalentsValidation(t)
            },
            [activeTab]
          )
        )
        .required(),
    [t, activeTab]
  );

  const formRef = useRef<SettingsModalFormRef<FieldValues>>(null);

  const statusActions = useProcessStatusActions({
    processId: params.processId,
    currentStatus: processData?.status,
    onChange: newStatus => {
      formRef.current?.setValue('status', newStatus);
    }
  });

  const { value: { canUpdateSettings, canManageTests, canAddTalents } = {} } = useProcessPermissions({
    variables: { processId: params.processId }
  });

  const toggleFullScreenMode = useCallback((isFullScreen: boolean) => {
    setIsFullScreenMode(isFullScreen);
  }, []);

  const handleBasicInfoSave = useCallback(
    async (values: BasicInfoFormValues) => {
      try {
        const basicInfoData = {
          name: values.name,
          assessmentType: values.assessmentType,
          description: values.description,
          startDate: values.availability?.start,
          endDate: values.availability?.end
        };
        await updateProcess({
          id: params.processId,
          data: basicInfoData
        });

        if (values.status && processData?.status !== values.status) {
          await statusActions.change(values.status);
        }

        showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
      } catch (error) {
        showMessage(t('settings:processSettings.form.alerts.saveError'));
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        refetchProcess();
      }
    },
    [updateProcess, params.processId, processData?.status, showMessage, t, refetchProcess, statusActions]
  );
  const handleTalentRegistrationSave = useCallback(
    async (values: TalentRegistrationFormValues) => {
      const talentRegistrationData = {
        external: values.external,
        internal: values.internal,
        translations: values.translations || {}
      };
      try {
        await updateTalentRegistrationForm({
          id: params.processId,
          data: talentRegistrationData
        });
        await queryClient.invalidateQueries(getGetProcessTalentRegistrationFormQueryKey(params.processId));

        showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
      } catch (error) {
        showMessage(t('settings:processSettings.form.alerts.saveError'));
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        refetchProcess();
      }
    },
    [updateTalentRegistrationForm, params.processId, queryClient, showMessage, t, refetchProcess]
  );

  const handleTestPropertiesSave = useCallback(
    async (values: TestPropertiesFormValues) => {
      try {
        const testSettingsData = testSettingsMappingPresave(values);

        await updateProcess({
          id: params.processId,
          data: testSettingsData
        });

        showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
      } catch (error) {
        showMessage(t('settings:processSettings.form.alerts.saveError'));
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        refetchProcess();
      }
    },
    [updateProcess, params.processId, showMessage, t, refetchProcess]
  );

  const handleTalentsSave = useCallback(
    async (values: TalentFormValues) => {
      try {
        if (!processData) throw new Error('No process data');
        const talentsToAdd = values?.talentsToAdd || [];
        if (talentsToAdd.length) {
          await addTalentsToProcess({ id: processData.id!, data: { talentIds: talentsToAdd } });
        }
        showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
      } catch (error) {
        showMessage(t('settings:processSettings.form.alerts.saveError'));
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        if (processData) {
          await queryClient.invalidateQueries(getGetProcessTalentsQueryKey(processData.id!));
        }
      }
    },
    [processData, showMessage, t, addTalentsToProcess, queryClient]
  );

  const handleAccessInvitationSave = useCallback(
    async (values: AccessInvitationFormValues) => {
      try {
        const accessInvitationData = accessInvitationMappingPresave(values);

        await updateProcess({
          id: params.processId,
          data: accessInvitationData
        });

        showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
      } catch (error) {
        showMessage(t('settings:processSettings.form.alerts.saveError'));
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        refetchProcess();
      }
    },
    [updateProcess, params.processId, showMessage, t, refetchProcess]
  );

  const onSaveMap = useMemo(
    () => ({
      [PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO]: (values: FieldValues) => handleBasicInfoSave(values?.[activeTab]),
      [PROCESS_SETTINGS_STEP_KEYS.TESTS]: (values: FieldValues) => Promise.resolve(values),
      [PROCESS_SETTINGS_STEP_KEYS.TALENTS]: (values: FieldValues) => handleTalentsSave(values?.[activeTab]),
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS]: (values: FieldValues) => Promise.resolve(values),
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION]: (values: FieldValues) =>
        handleTalentRegistrationSave(values?.[activeTab]),
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]: (values: FieldValues) =>
        handleTestPropertiesSave(values?.[activeTab]),
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]: (values: FieldValues) =>
        handleAccessInvitationSave(values?.[activeTab])
    }),
    [
      handleBasicInfoSave,
      handleTalentRegistrationSave,
      handleTalentsSave,
      handleTestPropertiesSave,
      handleAccessInvitationSave,
      activeTab
    ]
  );

  const isFooterShown = useMemo(() => isTabWithFooter(activeTab), [activeTab]);

  if (!defaultValues || isProcessLoading) {
    return <BackdropLoader />;
  }

  if (processError) {
    return <Typography color="error">{t('common:errors.loadingFailed')}</Typography>;
  }

  return (
    <OrganizationStructureProvider organizationSlug={processData?.organization?.slug as string}>
      <ReadonlyProvider readonly={!canUpdateSettings}>
        <SettingsModalForm
          head={<Typography>{t('processSettings.form.title')}</Typography>}
          defaultValues={defaultValues}
          onClose={onClose}
          validation={validations}
          sx={{
            '& .MuiDialogContent-root': {
              display: 'flex',
              flexDirection: 'row'
            },
            '& .MuiDialogTitle-root': {
              borderBottom: borders.default,
              borderColor: 'grey.400'
            }
          }}
          ref={formRef}
          onSave={
            onSaveMap?.[activeTab as keyof typeof onSaveMap] as ((values: FieldValues) => Promise<void>) | undefined
          }
          routePattern={`${parentRoutePattern}/settings`}
          isSaving={isSaving}
          closeOnSave={false}
        >
          <Stack
            sx={{
              paddingInlineStart: 2,
              paddingInlineEnd: 1,
              py: 3,
              bgcolor: 'grey.50',
              display: !isFullScreenMode ? 'flex' : 'none',
              height: '100%'
            }}
          >
            <VerticalTabs
              value={normalizedActiveTab}
              component={Stack}
              onChange={handleTabChange}
              sx={{
                width: SIDE_NAVIGATION_WIDTH_PX,
                minWidth: SIDE_NAVIGATION_WIDTH_PX
              }}
            >
              <VerticalTab
                value={PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO}
                label={t('processSettings.form.tabs.basicInfo')}
              />
              <VerticalTab value={PROCESS_SETTINGS_STEP_KEYS.TESTS} label={t('processSettings.form.tabs.tests')} />
              {canAddTalents && (
                <VerticalTab
                  value={PROCESS_SETTINGS_STEP_KEYS.TALENTS}
                  label={t('processSettings.form.tabs.talents')}
                />
              )}
              <VerticalTab
                value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS}
                label={t('processSettings.form.tabs.settings')}
              />
            </VerticalTabs>
          </Stack>
          <Stack flexGrow={1}>
            <Paper
              component={Stack}
              elevation={3}
              sx={{
                '.MuiDialogContent-root &.MuiPaper-root': {
                  m: 0,
                  width: '100%',
                  height: '100%',
                  bgcolor: 'background.default',
                  borderRadius: 0,
                  maxHeight: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  flexGrow: 1
                },
                p: 3
              }}
            >
              <TabPanel currentValue={normalizedActiveTab} value={PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO}>
                <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO}>
                  <BasicInfoTab
                    availableStatuses={statusActions.availableStatuses?.filter(Boolean) as ProcessResponseDTOStatus[]}
                  />
                </FieldPrefixProvider>
              </TabPanel>
              <TabPanel currentValue={normalizedActiveTab} value={PROCESS_SETTINGS_STEP_KEYS.TESTS}>
                <TestCatalogTab processId={params.processId} process={processData} readonly={!canManageTests} />
              </TabPanel>
              {canAddTalents && (
                <TabPanel currentValue={activeTab} value={PROCESS_SETTINGS_STEP_KEYS.TALENTS}>
                  <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.TALENTS}>
                    <TalentsTab processId={params.processId} process={processData} showTalentContactInfo />
                  </FieldPrefixProvider>
                </TabPanel>
              )}
              <TabPanel currentValue={normalizedActiveTab} value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS}>
                <SettingsTab
                  toggleFullScreenMode={toggleFullScreenMode}
                  isFullScreenMode={isFullScreenMode}
                  activeTab={activeTab}
                  onTabChange={newSubTab => setActiveTab(newSubTab)}
                  processId={params.processId}
                  organizationSlug={processData?.organization?.slug}
                  canUpdateAccessAndInvitation={canUpdateSettings}
                  canUpdateTestProperties={canUpdateSettings}
                  canUpdateTalentRegistration={canUpdateSettings}
                  canUpdateMessages={canUpdateSettings}
                />
              </TabPanel>
            </Paper>
            {isFooterShown && !isFullScreenMode && <CommonFormFooter sx={{ marginTop: 'auto' }} />}
          </Stack>
        </SettingsModalForm>
      </ReadonlyProvider>
    </OrganizationStructureProvider>
  );
};

const TABS_WITHOUT_FOOTER: PROCESS_SETTINGS_STEP_KEYS[] = [PROCESS_SETTINGS_STEP_KEYS.TESTS] as const;

const isTabWithFooter = (tab: string) => !TABS_WITHOUT_FOOTER.includes(tab as PROCESS_SETTINGS_STEP_KEYS);

export default ProcessSettingsForm;
